// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Edit,
  Trash2,
  Calendar,
  MapPin,
  Tag,
  Building2,
  Plus,
  CheckCircle,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import {
  useEquipmentDetail,
  useDeleteEquipment,
  useMaintenanceSchedules,
  useCreateMaintenanceSchedule,
  useUpdateMaintenanceSchedule,
  useDeleteMaintenanceSchedule,
} from '@/hooks/useCmms'
import { useUsers } from '@/hooks/useUsers'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday, utcDateOnly } from '@/lib/date'
import type { Equipment, MaintenanceSchedule } from '@/types'
import { ScheduleModal } from './components/ScheduleModal'

export function EquipmentDetailPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const equipmentId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const { timezone } = useLocalizationConfig()

  const { data: equipment, isLoading, isFetching } = useEquipmentDetail(equipmentId)
  const { data: schedulesData } = useMaintenanceSchedules(equipmentId)
  const deleteEquipment = useDeleteEquipment()
  const createSchedule = useCreateMaintenanceSchedule()
  const updateSchedule = useUpdateMaintenanceSchedule()
  const deleteSchedule = useDeleteMaintenanceSchedule()
  const { data: usersData } = useUsers()

  const schedules = schedulesData?.data || []
  const users = usersData?.data || []

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<MaintenanceSchedule | null>(null)
  const [showDeleteScheduleModal, setShowDeleteScheduleModal] = useState(false)
  const [deletingScheduleId, setDeletingScheduleId] = useState<number | null>(null)

  const handleDelete = async () => {
    await deleteEquipment.mutateAsync(equipmentId)
    navigate('/equipment')
  }

  const handleDeleteSchedule = async () => {
    if (!deletingScheduleId) return
    await deleteSchedule.mutateAsync({ id: deletingScheduleId, equipment_id: equipmentId })
    setShowDeleteScheduleModal(false)
    setDeletingScheduleId(null)
  }

  const addFrequencyInterval = (date: Date, frequency: string): Date => {
    const next = new Date(date)
    switch (frequency) {
      case 'daily': next.setDate(next.getDate() + 1); break
      case 'weekly': next.setDate(next.getDate() + 7); break
      case 'biweekly': next.setDate(next.getDate() + 14); break
      case 'monthly': next.setMonth(next.getMonth() + 1); break
      case 'quarterly': next.setMonth(next.getMonth() + 3); break
      case 'semi_annual': next.setMonth(next.getMonth() + 6); break
      case 'annual': next.setFullYear(next.getFullYear() + 1); break
    }
    return next
  }

  const handleSaveSchedule = async (formData: {
    title: string
    description: string
    frequency: string
    next_due_at: string
    active: boolean
    assigned_to_id: string
  }) => {
    const basePayload = {
      equipment_id: equipmentId,
      title: formData.title,
      description: formData.description || undefined,
      frequency: formData.frequency,
      active: formData.active,
      assigned_to_id: formData.assigned_to_id ? Number(formData.assigned_to_id) : null,
    }

    if (editingSchedule) {
      await updateSchedule.mutateAsync({
        id: editingSchedule.id,
        ...basePayload,
        next_due_at: formData.next_due_at || undefined,
      })
    } else {
      // Auto-create multiple schedules up to warranty expiry
      const warrantyEnd = equipment?.warranty_expires_at
        ? new Date(equipment.warranty_expires_at)
        : null
      // Fresh fallback (no explicit next_due_at) seeds from the account's local
      // "today", anchored at midnight UTC so the immediate toISOString().split
      // below yields that exact calendar day (B-020: never UTC-day-split a
      // fresh `new Date()` — it's the wrong day for Bangkok 00:00-07:00).
      const firstDue = formData.next_due_at
        ? new Date(formData.next_due_at)
        : new Date(`${localToday(timezone)}T00:00:00Z`)

      if (warrantyEnd && warrantyEnd > firstDue) {
        let dueDate = new Date(firstDue)
        while (dueDate <= warrantyEnd) {
          await createSchedule.mutateAsync({
            ...basePayload,
            next_due_at: utcDateOnly(dueDate),
          })
          dueDate = addFrequencyInterval(dueDate, formData.frequency)
        }
      } else {
        await createSchedule.mutateAsync({
          ...basePayload,
          next_due_at: formData.next_due_at || undefined,
        })
      }
    }
    setShowScheduleModal(false)
    setEditingSchedule(null)
  }

  const getStatusBadge = (item: Equipment) => {
    const variants: Record<Equipment['status'], 'success' | 'warning' | 'error' | 'ghost'> = {
      operational: 'success',
      needs_repair: 'warning',
      out_of_service: 'error',
      decommissioned: 'ghost',
    }
    return <Badge variant={variants[item.status]}>{t(`equipment_status.${item.status}`)}</Badge>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card><Skeleton className="h-48" /></Card>
      </div>
    )
  }

  if (!equipment) {
    return (
      <Card>
        <EmptyState
          title={t('empty.not_found')}
          action={<Button onClick={() => navigate('/equipment')}>{t('actions.back_to_list')}</Button>}
        />
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: t('equipment.title'), href: '/equipment' },
          { label: equipment.name },
        ]} />

        <PageHeader
          title={equipment.name}
          description={t('messages.created_on', { date: formatDate(equipment.created_at) })}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'edit',
            label: tc('actions.edit'),
            icon: Edit,
            to: `/equipment/${id}/edit`,
          }}
          overflowActions={[
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              destructive: true,
              onClick: () => setShowDeleteModal(true),
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Info */}
          {/* min-w-0: grid items default to min-width:auto; without it wide
              content inflates the card past the viewport on phones. */}
          <Card className="lg:col-span-2 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('equipment.detail')}</h2>
              {getStatusBadge(equipment)}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Tag className="w-4 h-4" /> {t('labels.category')}
                </dt>
                <dd className="font-medium break-words">{t(`categories.${equipment.category}`)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {t('labels.location')}
                </dt>
                <dd className="font-medium break-words">{equipment.location || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> {t('labels.property')}
                </dt>
                <dd className="font-medium break-words">{equipment.property?.name || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.manufacturer')}</dt>
                <dd className="font-medium break-words">{equipment.manufacturer || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.model_number')}</dt>
                <dd className="font-medium break-words">{equipment.model_number || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.serial_number')}</dt>
                <dd className="font-medium break-words">{equipment.serial_number || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.installed_at')}
                </dt>
                <dd className="font-medium">
                  {equipment.installed_at ? formatDate(equipment.installed_at) : '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.warranty_expires_at')}
                </dt>
                <dd className="font-medium">
                  {equipment.warranty_expires_at
                    ? formatDate(equipment.warranty_expires_at)
                    : '-'}
                </dd>
              </div>
            </dl>

            {equipment.notes && (
              <div className="border-t border-base-200 pt-4 mt-4">
                <h3 className="font-medium mb-2">{t('labels.notes')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{equipment.notes}</p>
              </div>
            )}
          </Card>

          {/* Images */}
          {equipment.image_urls && equipment.image_urls.length > 0 && (
            <Card>
              <h2 className="text-lg font-semibold mb-4">{t('labels.images')}</h2>
              <div className="grid grid-cols-2 gap-2">
                {equipment.image_urls.map((url, index) => (
                  <a
                    key={index}
                    href={`${import.meta.env.VITE_API_URL || ''}/api/images/${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-lg overflow-hidden border border-base-200 hover:border-primary transition-colors"
                  >
                    <img
                      src={`${import.meta.env.VITE_API_URL || ''}/api/images/${url}`}
                      alt={`${equipment.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Maintenance Schedules */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('maintenance_schedules.title')}</h2>
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => { setEditingSchedule(null); setShowScheduleModal(true) }}
            >
              {t('maintenance_schedules.add')}
            </Button>
          </div>
          {schedules.length === 0 ? (
            <p className="text-base-content/50 text-sm">{t('maintenance_schedules.no_schedules')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('labels.title')}</th>
                    <th>{t('labels.frequency')}</th>
                    <th>{t('labels.next_due_at')}</th>
                    <th>{t('labels.assigned_to')}</th>
                    <th>{tc('labels.status')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td className="font-medium">{schedule.title}</td>
                      <td>{t(`frequencies.${schedule.frequency}`)}</td>
                      <td>
                        {schedule.next_due_at
                          ? formatDate(schedule.next_due_at)
                          : '-'}
                      </td>
                      <td>{schedule.assigned_to?.name || '-'}</td>
                      <td>
                        {schedule.active ? (
                          <Badge variant="success">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {tc('status.active')}
                          </Badge>
                        ) : (
                          <Badge variant="ghost">{tc('status.inactive')}</Badge>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingSchedule(schedule); setShowScheduleModal(true) }}
                          >
                            {tc('actions.edit')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-error"
                            onClick={() => {
                              setDeletingScheduleId(schedule.id)
                              setShowDeleteScheduleModal(true)
                            }}
                          >
                            {tc('actions.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Recent Work Orders */}
        {equipment.recent_work_orders && equipment.recent_work_orders.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.recent_work_orders')}</h2>
            <div className="space-y-2">
              {equipment.recent_work_orders.map((wo) => (
                <button
                  key={wo.id}
                  onClick={() => navigate(`/work-orders/${wo.id}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-base-200 hover:border-primary hover:bg-base-200 transition-colors text-left"
                >
                  <div>
                    <div className="font-medium">{wo.title}</div>
                    <div className="text-sm text-base-content/60">
                      {formatDate(wo.created_at)}
                    </div>
                  </div>
                  <Badge
                    variant={
                      wo.status === 'completed' ? 'success' :
                      wo.status === 'in_progress' ? 'info' :
                      wo.status === 'cancelled' ? 'ghost' : 'warning'
                    }
                  >
                    {t(`work_order_status.${wo.status}`)}
                  </Badge>
                </button>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Delete Equipment Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('messages.delete_equipment_title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteEquipment.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_equipment_confirm', { name: equipment.name })}</p>
      </Modal>

      {/* Delete Schedule Modal */}
      <Modal
        isOpen={showDeleteScheduleModal}
        onClose={() => setShowDeleteScheduleModal(false)}
        title={t('messages.delete_schedule_title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteScheduleModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDeleteSchedule}
              loading={deleteSchedule.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_schedule_confirm')}</p>
      </Modal>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleModal
          schedule={editingSchedule}
          users={users}
          onClose={() => { setShowScheduleModal(false); setEditingSchedule(null) }}
          onSave={handleSaveSchedule}
          isSaving={createSchedule.isPending || updateSchedule.isPending}
        />
      )}
    </>
  )
}
